// src/components/DeviceCheckPage.tsx
import React, { useState, useEffect } from 'react';
import { 
  CircularProgress, 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Alert,
  AlertTitle,
  IconButton,
  Snackbar
} from '@mui/material';
import { 
  CheckCircle, 
  ErrorOutline, 
  Refresh, 
  ContentCopy,
  Security,
  LockPerson,
  ArrowBack
} from '@mui/icons-material';
import { deviceApi } from '../common/deviceApi';
import { generateDeviceId } from '../common/deviceFingerprint';
import { useAuth } from '../Auth';
import { useHistory } from 'react-router';
import { getUser } from '../common/utils';
import { IonContent, IonPage } from '@ionic/react';

const DeviceCheckPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [deviceCheckResult, setDeviceCheckResult] = useState<any>(null);
  const [deviceCode, setDeviceCode] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [deviceHash, setDeviceHash] = useState<string>('');
  
  const { isLoggedIn } = useAuth();
  const history = useHistory();
  const user_id = getUser().loginData.user_id;

  useEffect(() => {
    if (!isLoggedIn) {
      history.push('/login');
      return;
    }
    
    performDeviceCheck();
  }, []);

  const performDeviceCheck = async (): Promise<void> => {
    setLoading(true);
    //const user = getUser();
   // console.log("user in device check:", user);
    //return;
    try {
      // 1. Generate device ID
      const deviceId = generateDeviceId(user_id);
      setDeviceCode(deviceId.base64);
      setDeviceHash(deviceId.hash);
      
      // 2. Check device registration with backend
      const checkResult = await deviceApi.checkDeviceRegistration(user_id, deviceId.hash,deviceId.base64);
      console.log(checkResult)
      setDeviceCheckResult(checkResult);
      
      // 3. If device is registered, proceed to welcome page
      if (checkResult.status === true && checkResult.data?.device_registered === true) {
        setTimeout(() => {
          history.push('/login');
        }, 1500);
      }
      // If device is NOT registered, we stop here and show the blocking message
      // No redirect, no further action - the user is stuck on this page
      
    } catch (err: any) {
      console.error('Device check failed:', err);
      setDeviceCheckResult({ 
        status: false, 
        data: { device_registered: false } 
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string): void => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    });
  };

  const handleLogout = (): void => {
    history.push('/login');
  };

  const handleRetry = async (): Promise<void> => {
    setLoading(true);
    await performDeviceCheck();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          p: 3
        }}
      >
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h6" color="text.secondary">
          Checking device registration...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we verify your device
        </Typography>
      </Box>
    );
  }

  // DEVICE IS NOT REGISTERED - BLOCKING VIEW
  if (deviceCheckResult?.data?.device_registered === false) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          p: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'repeating-linear-gradient(45deg, #f5f5f5, #f5f5f5 10px, #e0e0e0 10px, #e0e0e0 20px)',
            opacity: 0.3,
            zIndex: 0
          }}
        />

        <Paper
          elevation={8}
          sx={{
            p: 5,
            maxWidth: 600,
            width: '100%',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'error.main'
          }}
        >
          {/* Warning Header */}
          <Box sx={{ mb: 4 }}>
            <ErrorOutline 
              sx={{ 
                fontSize: 80, 
                color: 'error.main',
                mb: 2,
                filter: 'drop-shadow(0px 4px 8px rgba(244, 67, 54, 0.3))'
              }} 
            />
            <Typography 
              variant="h4" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                color: 'error.dark',
                mb: 1
              }}
            >
              🔒 DEVICE NOT REGISTERED
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              Access Blocked - Security Protocol Active
            </Typography>
          </Box>

          {/* Main Alert */}
          <Alert 
            severity="error"
            icon={<LockPerson fontSize="large" />}
            sx={{ 
              mb: 4,
              textAlign: 'left',
              fontSize: '1.1rem',
              py: 2
            }}
          >
            <AlertTitle sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 1 }}>
              ⚠️ SECURITY RESTRICTION
            </AlertTitle>
            This device is not authorized to access the system. For security reasons, 
            all administrative accounts require device registration. Your access has been 
            blocked until this device is registered by an administrator.
          </Alert>

          {/* Device Code Section */}
          <Box 
            sx={{ 
              mb: 4, 
              p: 3, 
              bgcolor: 'warning.light', 
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'warning.main'
            }}
          >
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                color: 'warning.dark',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <Security /> DEVICE REGISTRATION CODE
            </Typography>
            
            <Paper
              elevation={2}
              sx={{
                p: 2,
                mt: 2,
                mb: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                position: 'relative'
              }}
            >
              <Typography 
                variant="body1" 
                sx={{ 
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  fontSize: '1rem',
                  lineHeight: 1.5,
                  letterSpacing: '0.5px'
                }}
              >
                {deviceCode}
              </Typography>
              
              <IconButton
                onClick={() => copyToClipboard(deviceCode)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
                size="small"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Paper>
            
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              {/* <strong>Hash:</strong> {deviceHash.substring(0, 20)}... */}
            </Typography>
            
            <Alert severity="info" sx={{ mb: 0 }}>
              <AlertTitle>Instructions</AlertTitle>
              1. Copy the device code above<br />
              2. Contact your system administrator<br />
              3. Provide them with this code<br />
              4. Wait for them to register your device<br />
              5. Refresh this page to continue
            </Alert>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRetry}
              disabled={loading}
              startIcon={<Refresh />}
              size="large"
            >
              {loading ? <CircularProgress size={24} /> : 'Check Registration Status'}
            </Button>
            
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleLogout}
              startIcon={<ArrowBack />}
              size="large"
            >
              Back to Login
            </Button>
          </Box>

          {/* Additional Info */}
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Note:</strong> This security measure protects sensitive administrative functions. 
              Device registration ensures that only authorized devices can access the system. 
              If you believe this is an error, contact your IT department immediately.
            </Typography>
          </Box>
        </Paper>

        {/* Copy Success Snackbar */}
        <Snackbar
          open={copySuccess}
          autoHideDuration={3000}
          onClose={() => setCopySuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" sx={{ width: '100%' }}>
            Device code copied to clipboard!
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // DEVICE IS REGISTERED - Success View
  return (
    <IonPage>
      <IonContent>
  <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 3
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center'
        }}
      >
        <Box sx={{ mb: 3 }}>
          <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
            Device Verified Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your device is approved and registered
          </Typography>
        </Box>

        <Box sx={{ mb: 3, p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, color: 'success.dark' }}>
            ✓ Access Granted
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting to your dashboard...
          </Typography>
          <CircularProgress 
            size={24} 
            sx={{ 
              mt: 2, 
              color: 'success.main' 
            }} 
          />
        </Box>

        <Alert severity="success" sx={{ mt: 3 }}>
          Security check passed. Your device is recognized and authorized.
        </Alert>
      </Paper>
    </Box>
      </IonContent>
    </IonPage>
  
  );
};

export default DeviceCheckPage;